import { useForm } from '@tanstack/react-form';
import { CheckCircle2, MailCheck, XCircle } from 'lucide-react';
import * as React from 'react';

import { useRequestMagicLink } from '@/api/use-identity';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

// =============================================================================
// landing-screen.tsx — Écran A-01 « Landing / accès magic-link » (F-01 amont).
// =============================================================================
// UC-F-001 : le fournisseur saisit son e-mail → l'API émet un magic-link envoyé
// par e-mail (Postmark = S7, stubé en S4 : la landing reçoit un ACCUSÉ « lien
// envoyé », jamais le lien en clair). L'UI ne fabrique ni ne hashe aucun token :
// l'autorité (TTL, plafond anti-abus 3/h, émission DE.MagicLinkIssued) = API.
//
// Conformité a11y (WCAG 2.1 AA, ST-NFR-03) :
//   - <h1> unique de page, champ labellisé (htmlFor/id), erreur en role="alert"
//     + aria-invalid sur le champ, succès en role="status" (annoncé), icônes
//     décoratives aria-hidden (jamais la couleur/icône seule porteuse de sens).
//   - État : in-memory (TanStack Form + Query), aucun localStorage (RULE_REF_006).

/**
 * Masque une adresse e-mail pour l'écran d'attente (DBU §3.1 l.2342 « [email masqué] ») :
 * conserve la 1ʳᵉ lettre du local et du domaine + le TLD, masque le reste. Aucune divulgation.
 */
function maskEmail(email: string): string {
  const at = email.indexOf('@');
  if (at <= 0) {
    return '•••';
  }
  const local = email.slice(0, at);
  const domain = email.slice(at + 1);
  const dot = domain.lastIndexOf('.');
  const maskedLocal = `${local[0]}•••`;
  if (dot <= 0) {
    return `${maskedLocal}@•••`;
  }
  return `${maskedLocal}@${domain[0]}•••${domain.slice(dot)}`;
}

export function LandingScreen(): React.JSX.Element {
  const request = useRequestMagicLink();

  const form = useForm({
    defaultValues: { email: '' },
    onSubmit: ({ value }) => {
      const email = value.email.trim();
      if (email.length === 0) {
        return;
      }
      request.mutate(email);
    },
  });

  // État « lien envoyé » DÉRIVÉ du résultat de la mutation (réactif) : pas d'état
  // local redondant. `request.reset()` (bouton « Saisir une autre adresse »)
  // efface ce résultat et réaffiche le formulaire.
  const sentTo = request.isSuccess ? request.data.email : null;

  const errorId = 'landing-email-error';
  const hasError = request.isError;

  return (
    <div className="mx-auto max-w-md space-y-6">
      <header className="space-y-1">
        <h1 className="text-2xl font-semibold tracking-tight">
          Accès fournisseur
        </h1>
        <p className="text-muted-foreground">
          Saisissez votre adresse e-mail professionnelle. Nous vous enverrons un
          lien de connexion sécurisé, valable un court instant et à usage unique.
        </p>
      </header>

      {sentTo !== null ? (
        <Card aria-labelledby="sent-title">
          <CardHeader>
            <div className="flex items-start gap-3">
              <MailCheck
                className="mt-0.5 h-6 w-6 shrink-0 text-success"
                aria-hidden="true"
              />
              <div className="space-y-1">
                <CardTitle id="sent-title" className="text-lg">
                  Lien envoyé
                </CardTitle>
                <CardDescription>
                  Si un compte est associé à <strong>{maskEmail(sentTo)}</strong>,
                  un lien de connexion vient d'être envoyé. Consultez votre boîte de
                  réception.
                </CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div
              role="status"
              className="flex items-start gap-3 rounded-md border border-success/40 bg-success/10 p-4"
            >
              <CheckCircle2
                className="mt-0.5 h-5 w-5 shrink-0 text-success"
                aria-hidden="true"
              />
              <p className="text-sm font-medium text-success">
                Demande prise en compte. Le lien expire après un court délai.
              </p>
            </div>
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                form.reset();
                request.reset();
              }}
            >
              Saisir une autre adresse
            </Button>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Recevoir un lien de connexion</CardTitle>
            <CardDescription>
              Aucun mot de passe : l'accès se fait par lien envoyé à votre e-mail.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form
              onSubmit={(event) => {
                event.preventDefault();
                event.stopPropagation();
                void form.handleSubmit();
              }}
              className="space-y-4"
              noValidate
            >
              <div className="space-y-2">
                <Label htmlFor="landing-email">Adresse e-mail</Label>
                <form.Field name="email">
                  {(field) => (
                    <Input
                      id="landing-email"
                      name={field.name}
                      type="email"
                      autoComplete="email"
                      inputMode="email"
                      value={field.state.value}
                      onChange={(e) => field.handleChange(e.target.value)}
                      onBlur={field.handleBlur}
                      aria-invalid={hasError}
                      aria-describedby={hasError ? errorId : undefined}
                      placeholder="prenom.nom@entreprise.fr"
                    />
                  )}
                </form.Field>
              </div>

              <div aria-live="polite" aria-atomic="true">
                {hasError ? (
                  <div
                    role="alert"
                    id={errorId}
                    className="flex items-start gap-3 rounded-md border border-destructive/40 bg-destructive/10 p-4"
                  >
                    <XCircle
                      className="mt-0.5 h-5 w-5 shrink-0 text-destructive"
                      aria-hidden="true"
                    />
                    <div className="space-y-1">
                      <p className="font-medium text-destructive">
                        Demande non envoyée
                      </p>
                      <p className="text-sm text-foreground">
                        {request.error?.message}
                      </p>
                    </div>
                  </div>
                ) : null}
              </div>

              <Button type="submit" disabled={request.isPending}>
                {request.isPending ? 'Envoi en cours…' : "Recevoir le lien d'accès"}
              </Button>
            </form>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
